/**
 * Enhanced GA Tracking – GTM-first dataLayer push
 * Events are consumed by GTM → GA4. Controlled by settings.enhancedTracking.enabled.
 *
 * On localhost (`window.env.local`), also sends each event via react-ga4 so hits
 * appear in Network/DebugView: GTM→GA4 tags often do not fire for localhost.
 */

import ReactGA from 'react-ga4';
import TagManager from 'react-gtm-module';

import {getFieldNameKey} from 'components/DynamicCoverage/Filters/utils';

let _settings = null;
let _dataLayerName = 'dataLayer';

/**
 * Initialize tracking with site settings.
 * Call when settings are loaded (e.g. in TrackingProvider).
 * When enhancedTracking.enabled, pushes to the GTM config with dataLayerName 'PALDataLayer'.
 * @param {Object} settings - Site settings (must include enhancedTracking, domain)
 */
export const init = (settings) => {
  if (!settings) {
    return;
  }
  _settings = settings;
  const gtmConfigs = Array.isArray(settings.gtm) ? settings.gtm : (settings.gtm ? [settings.gtm] : []);
  const palGtm = gtmConfigs.find((g) => g.dataLayerName === 'PALDataLayer');
  const defaultGtm = gtmConfigs[0];

  if (settings.enhancedTracking?.enabled && palGtm) {
    _dataLayerName = palGtm.dataLayerName;
  } else {
    _dataLayerName = (defaultGtm?.dataLayerName) || 'dataLayer';
  }
}

/**
 * Whether enhanced tracking is enabled for this site.
 * @returns {boolean}
 */
export function shouldTrack() {
  return Boolean(_settings?.enhancedTracking?.enabled);
}

/**
 * Mirror PAL events to GA4 on local dev only (same measurement ID as initializeReactGA).
 * @param {string} eventName
 * @param {Object} params
 */
const mirrorLocalGa4Event = (eventName, params) => {
  if (typeof window === 'undefined' || !window.env?.local) {
    return;
  }
  try {
    ReactGA.event(eventName, params);
  } catch {
    // GA may not be initialized yet on first paint
  }
};

/**
 * Push an event to the dataLayer. No-op if tracking is disabled.
 * @param {string} eventName - GTM custom event name
 * @param {Object} params - Event parameters
 */
const push = (eventName, params = {}) => {
  if (!shouldTrack()) {
    return;
  }
  const payload = {event: eventName, ...params};
  TagManager.dataLayer({
    dataLayer: payload,
    dataLayerName: _dataLayerName
  });
  mirrorLocalGa4Event(eventName, params);
};

/**
 * Build filter params for pals_filters_apply from filter state.
 * @param {Object} state - Filter state (selectedStates, selectedPlanTypes, selectedDynamicFilters)
 * @param {Object} settings - Site settings
 * @returns {Object} Params object
 */
const toPipeDelimited = (arr, getLabel = (x) => x) => {
  if (!Array.isArray(arr)) {
    return '';
  }
  return arr.map((x) => (x && typeof x === 'object' ? getLabel(x) : x)).filter(Boolean).join('|');
};

/**
 * Dynamic payer/PBM selections use the same key as DynamicFilter: getFieldNameKey(fieldName),
 * e.g. "payer|pbmClient" → state key "payer|pbmClient", not "payer" alone.
 */
const getPayerDynamicSelection = (selectedDynamicFilters, filterLayout) => {
  const selectedFilters = selectedDynamicFilters || {};
  if (Array.isArray(filterLayout)) {
    // Match the dynamic payer/PBM column by fieldName so we use the same state key as DynamicFilter (not a fixed "payer" key).
    const payerColumn = filterLayout.find(
      (c) => c.filter === 'dynamic' && c.fieldName && /\b(payer|pbmClient)\b/i.test(String(c.fieldName))
    );
    // If layout gives us a payer column, read its value by the normalized key; otherwise fall through to legacy keys below.
    if (payerColumn?.fieldName) {
      const key = getFieldNameKey(payerColumn.fieldName);
      if (key && selectedFilters[key] != null) {
        return selectedFilters[key];
      }
    }
  }

  // Fall back to legacy keys if no payer column is found in the layout.
  return selectedFilters.payer ?? selectedFilters.pbmClient ?? selectedFilters['payer|pbmClient'] ?? selectedFilters['pbmClient|payer'];
};

export const buildFiltersApplyParams = (state, settings) => {
  const payerRaw = getPayerDynamicSelection(state.selectedDynamicFilters, settings?.filterLayout);
  const payerFilter = Array.isArray(payerRaw) ? toPipeDelimited(payerRaw, (x) => x.label ?? x.name ?? x) : (payerRaw || '');
  const planTypeFilter = toPipeDelimited(state.selectedPlanTypes, (x) => x?.label ?? x?.name ?? x);
  const indicationRaw = state.selectedDynamicFilters?.indication;
  const indicationFilter = Array.isArray(indicationRaw) ? toPipeDelimited(indicationRaw, (x) => x.label ?? x.name ?? x) : (indicationRaw || '');
  const stateFilter = toPipeDelimited(state.selectedStates, (x) => x?.label ?? x?.name ?? x?.id ?? x);

  return {
    payer_filter: String(payerFilter),
    plan_type_filter: String(planTypeFilter),
    indication_filter: String(indicationFilter),
    state_filter: String(stateFilter),
    site_id: settings?.domain || ''
  };
};

/**
 * Event 1: pals_filters_apply – when results refresh due to filter changes
 */
export const filtersApply = (filterParams, resultsCount, siteId) => {
  push('pals_filters_apply', {
    ...filterParams,
    results_count: resultsCount,
    site_id: siteId || _settings?.domain || ''
  });
};

/**
 * Event 2: pals_result_click – click within a results table row
 * @param {string} clickType - coverage_document | pa_form | coverage_summary | detailed_brochure
 * @param {Object} rowData - Row data for params
 */
export const resultClick = (clickType, rowData) => {
  const params = {
    click_type: clickType,
    payer: rowData?.payer ?? '',
    plan_type: rowData?.planType ?? '',
    indication: rowData?.indication ?? '',
    row_states: Array.isArray(rowData?.states) ? rowData.states.join('|') : (rowData?.states ?? ''),
    benefit_type: rowData?.benefitType ?? rowData?.benefit ?? '',
    covered_status: rowData?.covered ?? rowData?.Covered ?? '',
    prior_auth_status: rowData?.priorAuth ?? rowData?.['Prior Authorization'] ?? '',
    last_review_date: rowData?.lastReviewDate ?? rowData?.['Last Day Reviewed'] ?? '',
    site_id: _settings?.domain || ''
  };
  if (rowData?.policyId) {
    params.policy_id = rowData.policyId;
  }
  push('pals_result_click', params);
};

/**
 * Event 3: pals_simple_brochure_click – top-level brochure button click
 */
export const simpleBrochureClick = (params = {}) => {
  push('pals_simple_brochure_click', {
    site_id: _settings?.domain || '',
    payer_filter: params.payer_filter ?? '',
    plan_type_filter: params.plan_type_filter ?? '',
    indication_filter: params.indication_filter ?? '',
    state_filter: params.state_filter ?? ''
  });
};

/**
 * Event 4: pals_request_rep_click – rep/request/contact links
 */
export const requestRepClick = (params) => {
  push('pals_request_rep_click', {
    site_id: _settings?.domain || '',
    link_name: params.link_name ?? '',
    link_url: params.link_url ?? '',
    payer_filter: params.payer_filter ?? '',
    plan_type_filter: params.plan_type_filter ?? '',
    indication_filter: params.indication_filter ?? '',
    state_filter: params.state_filter ?? ''
  });
};

/**
 * Event 5: pals_payer_link_modal – ExitModal outcome (leaves | stays)
 */
export const externalLinkExitModal = (outcome) => {
  push('pals_payer_link_modal', {
    outcome: outcome === 'leaves' || outcome === 'stays' ? outcome : 'stays',
    site_id: _settings?.domain || ''
  });
};
